/**
 * BOQ Purchase History
 * Timeline view of all purchases for a BOQ item.
 */

import React from 'react';
import {
  Box, Typography, Chip, Divider, Avatar
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { brand, status as statusColors } from '../../theme/colors';

const fmt = (n) => parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function BOQPurchaseHistory({ purchases = [], estimatedUnitPrice }) {
  if (purchases.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
        <ShoppingCartIcon sx={{ fontSize: 32, mb: 1, opacity: 0.4 }} />
        <Typography variant="body2">No purchases logged yet</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {purchases.map((p, idx) => {
        const priceDelta = parseFloat(p.actual_unit_price) - parseFloat(estimatedUnitPrice || 0);
        const profile = p.purchaser_profile || {};

        return (
          <Box key={p.id}>
            {idx > 0 && <Divider sx={{ my: 1.5 }} />}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: brand.primary, fontSize: '0.75rem' }}>
                {(profile.first_name || '?')[0]}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {profile.first_name} {profile.last_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(p.purchase_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                  <Chip
                    label={`${p.quantity} × $${fmt(p.actual_unit_price)} = $${fmt(p.total_cost)}`}
                    size="small"
                    sx={{ fontFamily: 'monospace', fontWeight: 600, bgcolor: brand.primary + '12', color: brand.primaryDark }}
                  />
                  {priceDelta !== 0 && (
                    <Chip
                      label={`${priceDelta > 0 ? '▲' : '▼'} $${fmt(Math.abs(priceDelta))}`}
                      size="small"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.7rem',
                        bgcolor: priceDelta > 0 ? statusColors.warningBg : statusColors.successBg,
                        color: priceDelta > 0 ? statusColors.warningDark : statusColors.successDark,
                        fontWeight: 600
                      }}
                    />
                  )}
                </Box>
                {p.supplier_name && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    from {p.supplier_name}
                  </Typography>
                )}
                {p.notes && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
                    {p.notes}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
